import { format, isValid } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

export const DATE_TIME_WINDOW_OPERATORS = [
  { id: 'within', name: 'AUTOMATION.FLOW.DATE_TIME_WINDOW.OPERATORS.WITHIN' },
  {
    id: 'not_within',
    name: 'AUTOMATION.FLOW.DATE_TIME_WINDOW.OPERATORS.NOT_WITHIN',
  },
];

export const DATE_TIME_WINDOW_TYPES = [
  {
    id: 'absolute_range',
    name: 'AUTOMATION.FLOW.DATE_TIME_WINDOW.TYPES.ABSOLUTE_RANGE',
  },
  {
    id: 'business_hours',
    name: 'AUTOMATION.FLOW.DATE_TIME_WINDOW.TYPES.BUSINESS_HOURS',
  },
];

export const DATE_TIME_WINDOW_BRANCH_KEYS = {
  success: 'success_next_step_id',
  failure: 'failure_next_step_id',
};

export const createWorkflowStepId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `step_${Math.random().toString(36).slice(2, 10)}`;
};

export const getDefaultDateTimeWindowParams = timezone => ({
  window_type: 'business_hours',
  operator: 'within',
  start_at: '',
  end_at: '',
  timezone: timezone || 'UTC',
  success_next_step_id: null,
  failure_next_step_id: null,
});

export const normalizeDateTimeWindowPayload = (
  payload,
  defaultTimezone = 'UTC'
) => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return getDefaultDateTimeWindowParams(defaultTimezone);
  }

  return {
    ...getDefaultDateTimeWindowParams(defaultTimezone),
    ...payload,
    window_type:
      payload.window_type === 'business_hours'
        ? 'business_hours'
        : 'absolute_range',
    operator: payload.operator === 'not_within' ? 'not_within' : 'within',
    timezone: payload.timezone || defaultTimezone || 'UTC',
    success_next_step_id: payload.success_next_step_id || null,
    failure_next_step_id: payload.failure_next_step_id || null,
  };
};

export const toDateTimeWindowForm = (payload, defaultTimezone = 'UTC') => {
  const normalized = normalizeDateTimeWindowPayload(payload, defaultTimezone);
  const timezone = normalized.timezone || defaultTimezone || 'UTC';
  const startDate = normalized.start_at
    ? utcToZonedTime(normalized.start_at, timezone)
    : null;
  const endDate = normalized.end_at
    ? utcToZonedTime(normalized.end_at, timezone)
    : null;

  return {
    ...normalized,
    start_date: startDate,
    start_time: startDate ? format(startDate, 'hh:mm a') : '',
    end_date: endDate,
    end_time: endDate ? format(endDate, 'hh:mm a') : '',
  };
};

export const combineDateAndTimeForTimezone = (date, time, timezone) => {
  if (!date || !time || !timezone) return '';

  const normalizedDate = date instanceof Date ? date : new Date(date);
  if (!isValid(normalizedDate)) return '';

  const datePart = format(normalizedDate, 'yyyy-MM-dd');
  const utcDate = zonedTimeToUtc(`${datePart} ${time}`, timezone);
  return utcDate.toISOString();
};

export const toDateTimeWindowPayload = params => {
  const timezone = params?.timezone || 'UTC';
  const windowType =
    params?.window_type === 'business_hours'
      ? 'business_hours'
      : 'absolute_range';

  return normalizeDateTimeWindowPayload(
    {
      window_type: windowType,
      operator: params?.operator || 'within',
      start_at:
        windowType === 'absolute_range'
          ? combineDateAndTimeForTimezone(
              params?.start_date,
              params?.start_time,
              timezone
            )
          : '',
      end_at:
        windowType === 'absolute_range'
          ? combineDateAndTimeForTimezone(
              params?.end_date,
              params?.end_time,
              timezone
            )
          : '',
      timezone,
      success_next_step_id: params?.success_next_step_id || null,
      failure_next_step_id: params?.failure_next_step_id || null,
    },
    timezone
  );
};

export const getDateTimeWindowBranchTargetId = (params, branchKey) => {
  const branchField = DATE_TIME_WINDOW_BRANCH_KEYS[branchKey];
  return branchField ? params?.[branchField] || null : null;
};

export const setDateTimeWindowBranchTargetId = (
  params,
  branchKey,
  targetId
) => {
  const branchField = DATE_TIME_WINDOW_BRANCH_KEYS[branchKey];
  if (!branchField) return params;

  return {
    ...params,
    [branchField]: targetId || null,
  };
};

export const formatDateTimeWindowSummary = (params, timezoneLabel = '') => {
  const normalized = params?.start_date
    ? toDateTimeWindowPayload(params)
    : normalizeDateTimeWindowPayload(params);
  if (normalized.window_type === 'business_hours') {
    const operatorLabel =
      normalized.operator === 'not_within'
        ? 'Not within business hours'
        : 'Within business hours';

    return `${operatorLabel} - ${timezoneLabel || normalized.timezone}`;
  }

  if (!normalized.start_at || !normalized.end_at || !normalized.timezone) {
    return '';
  }

  const timezone = normalized.timezone;
  const startDate = utcToZonedTime(normalized.start_at, timezone);
  const endDate = utcToZonedTime(normalized.end_at, timezone);
  if (!isValid(startDate) || !isValid(endDate)) {
    return '';
  }

  const operatorLabel =
    normalized.operator === 'not_within' ? 'Not within' : 'Within';

  return `${operatorLabel} ${format(startDate, 'd MMM yy, HH:mm')} to ${format(
    endDate,
    'd MMM yy, HH:mm'
  )} - ${timezoneLabel || timezone}`;
};
